import { Platform } from "./db"
import { PROSPECT_PHONE_TYPE } from 'interfaces/db';

export enum SelectionType {
    ME = 'me',
    OVERALL_TEAM = 'overall_team',
    SUB_TEAM = 'sub_team',
    USER = 'user'
}

export type UserSelection = {
    type: SelectionType;
    id: string;
};

// Re-export the mapping from db.tsx for backwards compatibility
export { PHONE_TYPE_TO_HUMAN_READABLE } from 'interfaces/db';

export enum MusicWebsite {
    SPOTIFY='SPOTIFY',
    YOUTUBE='YOUTUBE'
}

export enum DialingMode {
    MULTI_NUMBER_ATTEMPT = 'MULTI_NUMBER_ATTEMPT',
    SINGLE_NUMBER_ATTEMPT = 'SINGLE_NUMBER_ATTEMPT'
}

export enum OutreachModalOnClickSetting {
    RECORD_ALWAYS = 'RECORD_ALWAYS',
    RECORD_ONE_PARTY = 'RECORD_ONE_PARTY'
}

export enum NumberRotationStrategy {
    AUTO_ROTATE = 'AUTO_ROTATE_ALL',
    GEOGRAPHICALLY_CLOSEST = 'GEOGRAPHICALLY_CLOSEST_ALL'
}

export enum PhoneKeys  {
  ACTIVE = 'active',
  DOUBLE_TAPS = 'double_taps',
  ORDER = 'order'
}

export type PhonePreference = {
    [PhoneKeys.ACTIVE]: boolean,
    [PhoneKeys.DOUBLE_TAPS]: number,
    [PhoneKeys.ORDER]: number
}

export enum SETTING  {
    TIME_TO_DIAL='time_to_dial',
    TIME_TO_LOG='time_to_log',
    AUTO_PROGRESS_LIVE_ACTIVE='auto_progress_live_active',
    AUTO_PROGRESS_TIME='auto_progress_time',
    HANGUP_RINGING_ACTIVE='hangup_ringing_active',
    HANGUP_RINGING_TIME='hangup_ringing_time',
    HANGUP_VOICEMAIL_ACTIVE='hangup_voicemail_active',
    HANGUP_VOICEMAIL_TIME='hangup_voicemail_time',
    MUSIC_WEBSITE='music_website',
    LOCAL_CALL_TIME_MIN='local_call_time_min',
    LOCAL_CALL_TIME_MAX='local_call_time_max',
    VALIDATE_TIMEZONE_VIA_NUMBER='validate_timezone_via_number',
    DIALING_MODE='dialing_mode',
    PHONE_PREFERENCE='phone_preference',
    LINKEDIN_AUTO_OPEN='linkedin_auto_open',
    COMPANY_WEBSITE_AUTO_OPEN='company_website_auto_open',
    SALESFORCE_URL_AUTO_OPEN='salesforce_url_auto_open',
    NO_AUTO_COMPLETE_TASK='no_auto_complete_task',
    ACTION_ON_BAD_NUMBER='action_on_bad_number',
    NO_SUMMARY_AUTOMATION='no_summary_automation',
    HIDE_WRONG_NUMBER_ELEMENT='hide_wrong_number_element',
    NO_LOG_ONLY='no_log_only',
    PRIMARY_ALWAYS='primary_always',
    PRIMARY_FIRST='primary_first',
    VERIFIED_ALWAYS='verified_always',
    DNC_ALWAYS='dnc_always',
    OUTREACH_MODAL_CLICK_SETTING='outreach_modal_click_setting',
    ALL_RECORDS='all_records',
    POWER_CALL_SHOULD_RING='power_call_should_ring',
    MAX_NUMBER_LINES='max_number_lines',
    INCOMING_CALL_MUTED='incoming_call_muted',
    TEAM_CALL_MUTED='team_call_muted',
    AUTOMATIC_EXTENSION_NUMBER='automatic_extension_number',
    ADD_TRANSCRIPT_TO_SUMMARY_SETTING='add_transcript_to_summary_setting',
    PLAY_BEEP_ON_CONNECT='play_beep_on_connect',
    UNIVERSAL_CONTROLLER_ENABLED='universal_controller_enabled',
    NO_NAVIGATION_ON_CONNECT='no_navigation_on_connect',
    NO_CANCEL_CALLS='no_cancel_calls',
    DEFAULT_NUMBER_ROTATION_STRATEGY='default_number_rotation_strategy'
}

export type AutoDialerSettingValues = {
    [SETTING.TIME_TO_DIAL]: number | null;
    [SETTING.TIME_TO_LOG]: number | null;
    [SETTING.AUTO_PROGRESS_LIVE_ACTIVE]: boolean | null;
    [SETTING.AUTO_PROGRESS_TIME]: number | null;
    [SETTING.HANGUP_RINGING_ACTIVE]: boolean | null;
    [SETTING.HANGUP_RINGING_TIME]: number | null;
    [SETTING.HANGUP_VOICEMAIL_ACTIVE]: boolean | null;
    [SETTING.HANGUP_VOICEMAIL_TIME]: number | null;
    [SETTING.MUSIC_WEBSITE]: MusicWebsite | null;
    [SETTING.LOCAL_CALL_TIME_MIN]: number | null;
    [SETTING.LOCAL_CALL_TIME_MAX]: number | null;
    [SETTING.VALIDATE_TIMEZONE_VIA_NUMBER]: boolean | null;
    [SETTING.DIALING_MODE]: DialingMode | null;
    [SETTING.PHONE_PREFERENCE]: Record<PROSPECT_PHONE_TYPE, PhonePreference> | null;
    [SETTING.LINKEDIN_AUTO_OPEN]: boolean | null;
    [SETTING.COMPANY_WEBSITE_AUTO_OPEN]: boolean | null;
    [SETTING.SALESFORCE_URL_AUTO_OPEN]: boolean | null;
    [SETTING.NO_AUTO_COMPLETE_TASK]: boolean | null;
    [SETTING.ACTION_ON_BAD_NUMBER]: string | null;
    [SETTING.NO_SUMMARY_AUTOMATION]: boolean | null;
    [SETTING.HIDE_WRONG_NUMBER_ELEMENT]: boolean | null;
    [SETTING.NO_LOG_ONLY]: boolean | null;
    [SETTING.PRIMARY_ALWAYS]: boolean | null;
    [SETTING.PRIMARY_FIRST]: boolean | null;
    [SETTING.VERIFIED_ALWAYS]: boolean | null;
    [SETTING.DNC_ALWAYS]: boolean | null;
    [SETTING.OUTREACH_MODAL_CLICK_SETTING]: OutreachModalOnClickSetting | null;
    [SETTING.ALL_RECORDS]: boolean | null;
    [SETTING.POWER_CALL_SHOULD_RING]: boolean | null;
    [SETTING.MAX_NUMBER_LINES]: number | null;
    [SETTING.INCOMING_CALL_MUTED]: boolean | null;
    [SETTING.TEAM_CALL_MUTED]: boolean | null;
    [SETTING.AUTOMATIC_EXTENSION_NUMBER]: string | null;
    [SETTING.ADD_TRANSCRIPT_TO_SUMMARY_SETTING]: boolean | null;
    [SETTING.PLAY_BEEP_ON_CONNECT]: boolean | null;
    [SETTING.UNIVERSAL_CONTROLLER_ENABLED]: boolean | null;
    [SETTING.NO_NAVIGATION_ON_CONNECT]: boolean | null;
    [SETTING.NO_CANCEL_CALLS]: boolean | null;
    [SETTING.DEFAULT_NUMBER_ROTATION_STRATEGY]: NumberRotationStrategy | null;
}

export interface AutoDialerSettingSpec {
    user_id: string
    user_group_id: string
    team_id: string
    locked_user_id: string
    platform: Platform
}

export type AutoDialerSetting = AutoDialerSettingSpec & AutoDialerSettingValues;

export function jsonSafeToAutodialerSetting(v: Record<string, any>): AutoDialerSetting {
    return {
      user_id: v.user_id,
      user_group_id: v.user_group_id,
      team_id: v.team_id,
      locked_user_id: v.locked_user_id,
      platform: v.platform,
      [SETTING.TIME_TO_DIAL]: v.time_to_dial,
      [SETTING.TIME_TO_LOG]: v.time_to_log,
      [SETTING.AUTO_PROGRESS_LIVE_ACTIVE]: v.auto_progress_live_active,
      [SETTING.AUTO_PROGRESS_TIME]: v.auto_progress_time,
      [SETTING.HANGUP_RINGING_ACTIVE]: v.hangup_ringing_active,
      [SETTING.HANGUP_RINGING_TIME]: v.hangup_ringing_time,
      [SETTING.HANGUP_VOICEMAIL_ACTIVE]: v.hangup_voicemail_active,
      [SETTING.HANGUP_VOICEMAIL_TIME]: v.hangup_voicemail_time,
      [SETTING.MUSIC_WEBSITE]: v.music_website,
      [SETTING.LOCAL_CALL_TIME_MIN]: v.local_call_time_min,
      [SETTING.LOCAL_CALL_TIME_MAX]: v.local_call_time_max,
      [SETTING.VALIDATE_TIMEZONE_VIA_NUMBER]: v.validate_timezone_via_number,
      [SETTING.DIALING_MODE]: v.dialing_mode,
      [SETTING.PHONE_PREFERENCE]: v.phone_preference ? JSON.parse(v.phone_preference) : null,
      [SETTING.LINKEDIN_AUTO_OPEN]: v.linkedin_auto_open,
      [SETTING.COMPANY_WEBSITE_AUTO_OPEN]: v.company_website_auto_open,
      [SETTING.SALESFORCE_URL_AUTO_OPEN]: v.salesforce_url_auto_open,
      [SETTING.NO_AUTO_COMPLETE_TASK]: v.no_auto_complete_task,
      [SETTING.ACTION_ON_BAD_NUMBER]: v.action_on_bad_number,
      [SETTING.NO_SUMMARY_AUTOMATION]: v.no_summary_automation,
      [SETTING.HIDE_WRONG_NUMBER_ELEMENT]: v.hide_wrong_number_element,
      [SETTING.NO_LOG_ONLY]: v.no_log_only,
      [SETTING.PRIMARY_ALWAYS]: v.primary_always,
      [SETTING.PRIMARY_FIRST]: v.primary_first,
      [SETTING.VERIFIED_ALWAYS]: v.verified_always,
      [SETTING.DNC_ALWAYS]: v.dnc_always,
      [SETTING.OUTREACH_MODAL_CLICK_SETTING]: v.outreach_modal_click_setting,
      [SETTING.ALL_RECORDS]: v.all_records,
      [SETTING.POWER_CALL_SHOULD_RING]: v.power_call_should_ring,
      [SETTING.MAX_NUMBER_LINES]: v.max_number_lines,
      [SETTING.INCOMING_CALL_MUTED]: v.incoming_call_muted,
      [SETTING.TEAM_CALL_MUTED]: v.team_call_muted,
      [SETTING.AUTOMATIC_EXTENSION_NUMBER]: v.automatic_extension_number,
      [SETTING.ADD_TRANSCRIPT_TO_SUMMARY_SETTING]: v.add_transcript_to_summary_setting,
      [SETTING.PLAY_BEEP_ON_CONNECT]: v.play_beep_on_connect,
      [SETTING.UNIVERSAL_CONTROLLER_ENABLED]: v.universal_controller_enabled,
      [SETTING.NO_NAVIGATION_ON_CONNECT]: v.no_navigation_on_connect,
      [SETTING.NO_CANCEL_CALLS]: v.no_cancel_calls,
      [SETTING.DEFAULT_NUMBER_ROTATION_STRATEGY]: v.default_number_rotation_strategy
    }
  }

export function jsonSafeToAutodialerSettings(v: Record<string, any>): AutoDialerSetting[] { 
    return v.map((x: Record<string, any>) => jsonSafeToAutodialerSetting(x))
}

// Type utilities for inferring control types
export type SettingValueType<T extends SETTING> = AutoDialerSettingValues[T];

// Control type enumeration
export enum ControlType {
    RANGE = 'range',
    TOGGLE = 'toggle', 
    SELECT = 'select',
    CUSTOM = 'custom'
}
export enum AutomationType {
  DISPOSITION = 'DISPOSITION',
  SENTIMENT = 'SENTIMENT',
  CUSTOM = 'CUSTOM',
  PURPOSE = 'PURPOSE',
  VM_INFO = 'VM_INFO'
}

export const AUTOMATION_TYPE_TO_HUMAN_READABLE: {
  [k in AutomationType]: string
} = {
  [AutomationType.DISPOSITION]: 'Disposition',
  [AutomationType.SENTIMENT]: 'Sentiment',
  [AutomationType.CUSTOM]: 'Custom',
  [AutomationType.PURPOSE]: 'Purpose',
  [AutomationType.VM_INFO]: 'Voicemail'
}

export enum TrellusDisposition {
  NO_ANSWER = 'NO_ANSWER',
  LEFT_VOICEMAIL = 'LEFT_VOICEMAIL',
  WRONG_NUMBER = 'WRONG_NUMBER',
  GATEKEEPER = 'GATEKEEPER',
  NOT_INTERESTED = 'NOT_INTERESTED',
  OTHER_ANSWERED = 'OTHER_ANSWERED',
  SPECIAL_MATCH_ALL = 'SPECIAL_MATCH_ALL'
}

export const TrellusDispositionIsPrediction: {
  [k in TrellusDisposition]: boolean
} = {
  [TrellusDisposition.NO_ANSWER]: true,
  [TrellusDisposition.LEFT_VOICEMAIL]: true,
  [TrellusDisposition.WRONG_NUMBER]: true,
  [TrellusDisposition.GATEKEEPER]: true,
  [TrellusDisposition.NOT_INTERESTED]: true,
  [TrellusDisposition.OTHER_ANSWERED]: true,
  [TrellusDisposition.SPECIAL_MATCH_ALL]: false
}

export const TrellusDispositionToHumanReadable: {
  [k in TrellusDisposition]: string
} = {
  [TrellusDisposition.NO_ANSWER]: "No Answer",
  [TrellusDisposition.LEFT_VOICEMAIL]: "Left VM",
  [TrellusDisposition.WRONG_NUMBER]: "Wrong/Bad Number",
  [TrellusDisposition.GATEKEEPER]: "Hit Gatekeeper",
  [TrellusDisposition.NOT_INTERESTED]: "Not interested",
  [TrellusDisposition.OTHER_ANSWERED]: "Quick conversation",
  [TrellusDisposition.SPECIAL_MATCH_ALL]: ''
}

export type AutoDialerMapping = {
  user_id: string
  user_group_id: string
  team_id: string
  locked_user_id: string
  platform: Platform
  automation_type: AutomationType
  trellus_disposition: TrellusDisposition
  value: string
}

export type AutoDialerMappingOptions = {
  user_id: string
  platform: Platform
  automation_type: AutomationType
  options: string[]
}


export function jsonSafeToAutoDialerMappingOptions(v: Record<string, any>): AutoDialerMappingOptions {
  let parsedOptions: string[] = [];
  try {
    parsedOptions = v.options ? JSON.parse(v.options) : [];
  } catch (error) {
    if (Array.isArray(v.options)) {
      parsedOptions = v.options;
    } else {
      parsedOptions = [];
    }
  }
  return {
    user_id: v.user_id,
    platform: v.platform,
    automation_type: v.automation_type,
    options: parsedOptions
  }
}

export function jsonSafeToAutodialerMappingOptionsList(v: Record<string, any>): AutoDialerMappingOptions[] {
  return v.map((x: Record<string, any>) => jsonSafeToAutoDialerMappingOptions(x))
}
